import { fetchJSON as fetchAPI } from './client';

const BASE = import.meta.env.VITE_SAAS_API ?? 'http://localhost:8166';

export type SaaSTenant = {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  description?: string;
  status: string;
  plan?: string;
  region?: string;
  branding?: TenantBranding;
  profile?: OrganizationProfile;
  provisioned_at?: string;
  created_at: string;
  updated_at: string;
};

export type OrganizationProfile = {
  legal_name?: string;
  gst_number?: string;
  pan?: string;
  registration_number?: string;
  business_type?: string;
  country?: string;
  timezone?: string;
  currency?: string;
  language?: string;
};

export type TenantBranding = {
  logo_url?: string;
  favicon_url?: string;
  custom_domain?: string;
  primary_color?: string;
  accent_color?: string;
  display_name?: string;
  theme?: string;
  typography?: string;
  css_overrides?: string;
};

export type SaaSOrganization = {
  id: string;
  name: string;
  slug: string;
  status: string;
  profile?: OrganizationProfile;
  tenant_count: number;
  created_at: string;
};

export type SaaSDashboard = {
  total_tenants: number;
  active_tenants: number;
  suspended_tenants: number;
  pending_tenants: number;
  total_organizations: number;
  total_users: number;
  total_storage_gb: number;
  total_ai_credits: number;
  recent_tenants?: SaaSTenant[];
  top_usage?: TenantUsage[];
  platform_health?: { module: string; status: string; latency_ms: number }[];
  generated_at: string;
};

export type TenantUsage = {
  tenant_id: string;
  period: string;
  active_users: number;
  module_count: number;
  storage_used_gb: number;
  ai_used_credits: number;
  api_calls: number;
  resources: {
    cpu_hours: number;
    memory_gb: number;
    storage_gb: number;
    ai_usage_credits: number;
    api_requests: number;
  };
};

export type ProvisioningResult = {
  tenant_id: string;
  status: string;
  steps: { name: string; status: string; detail?: string }[];
  api_key?: string;
  schema_name?: string;
};

export type MarketplaceConnection = {
  id: string;
  tenant_id: string;
  marketplace: string;
  account_id?: string;
  status: string;
};

export type CreateTenantInput = {
  name: string;
  organization_id?: string;
  slug?: string;
  description?: string;
  plan?: string;
  provision?: boolean;
};

export const saasApi = {
  dashboard: () => fetchAPI<SaaSDashboard>(BASE, '/api/v1/saas/dashboard'),
  tenants: () => fetchAPI<SaaSTenant[]>(BASE, '/api/v1/tenants'),
  getTenant: (id: string) => fetchAPI<SaaSTenant>(BASE, `/api/v1/tenants/${id}`),
  createTenant: (body: CreateTenantInput) =>
    fetchAPI<SaaSTenant>(BASE, '/api/v1/tenants', { method: 'POST', body: JSON.stringify(body) }),
  updateTenant: (id: string, body: Partial<SaaSTenant>) =>
    fetchAPI<SaaSTenant>(BASE, `/api/v1/tenants/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  provision: (id: string) => fetchAPI<ProvisioningResult>(BASE, `/api/v1/tenants/${id}/provision`, { method: 'POST' }),
  suspend: (id: string) => fetchAPI<SaaSTenant>(BASE, `/api/v1/tenants/${id}/suspend`, { method: 'POST' }),
  activate: (id: string) => fetchAPI<SaaSTenant>(BASE, `/api/v1/tenants/${id}/activate`, { method: 'POST' }),
  organizations: () => fetchAPI<SaaSOrganization[]>(BASE, '/api/v1/organizations'),
  getOrganization: (id: string) => fetchAPI<SaaSOrganization>(BASE, `/api/v1/organizations/${id}`),
  tenantUsage: (tenantId?: string) =>
    fetchAPI<TenantUsage[]>(BASE, `/api/v1/tenant-usage${tenantId ? `?tenant_id=${tenantId}` : ''}`),
  getBranding: (tenantId: string) => fetchAPI<TenantBranding>(BASE, `/api/v1/tenants/${tenantId}/branding`),
  updateBranding: (tenantId: string, body: TenantBranding) =>
    fetchAPI<TenantBranding>(BASE, `/api/v1/tenants/${tenantId}/branding`, { method: 'PUT', body: JSON.stringify(body) }),
  marketplaces: (tenantId: string) => fetchAPI<MarketplaceConnection[]>(BASE, `/api/v1/tenants/${tenantId}/marketplaces`),
};
